import { useEffect, useRef, useState } from 'react';
import { Shell } from '../../components/layout/Shell.jsx';
import { LANG } from '../../lib/i18n';
import { C } from '../../theme/palette';

export function PresentationPage({ onNav, soc, estMixte, univers, setUnivers }) {
  const _SLIDES_ALL = [
    {
      icon: '🇨🇭',
      k: 'intro',
      titre: 'SwissTech',
      sous: 'La gestion complète de votre PME suisse',
      texte:
        'SwissTech : la gestion complète de votre PME suisse, réunie dans une seule application. Devis, factures, chantiers, salaires, stock et comptabilité. Tout votre métier, dans votre poche.',
    },
    {
      icon: '📄',
      k: 'devis',
      titre: 'Devis & Offres',
      sous: 'Six styles A4, acceptation en ligne',
      texte:
        "Créez des devis professionnels en quelques secondes, avec six styles de présentation. Votre client accepte et signe directement en ligne, et le devis se transforme en facture d'un seul geste.",
    },
    {
      icon: '🧾',
      k: 'factures',
      titre: 'Factures & QR-facture',
      sous: 'Conforme à la norme suisse',
      texte:
        'Émettez des factures avec la QR-facture suisse. Suivez les paiements, envoyez des rappels, encaissez des acomptes, et remboursez un trop-perçu en cas de double paiement.',
    },
    {
      m: 'bat',
      icon: '🏗️',
      k: 'chantiers',
      titre: 'Chantiers',
      sous: 'Du planning à la réception',
      texte:
        'Pilotez vos chantiers de A à Z : planning des équipes, heures pointées, photos, plans, sous-traitants, avenants signés, réception des travaux et portail client. Tout est centralisé.',
    },
    {
      m: 'bat',
      icon: '👷',
      k: 'paie',
      titre: 'Équipe & Paie suisse',
      sous: 'AVS, LPP, impôt source, 13e',
      texte:
        'Calculez les salaires suisses automatiquement : AVS, chômage, accidents, LPP, impôt à la source et treizième salaire. Générez la fiche de salaire en PDF en un clic.',
    },
    {
      m: 'bat',
      icon: '📲',
      k: 'ouvrier',
      titre: 'Espace employé',
      sous: 'Le terrain dans la poche',
      texte:
        'Vos employés pointent leurs heures, envoient leurs rapports journaliers avec photos, déclarent leurs congés et voient leur chantier du jour, depuis leur téléphone.',
    },
    {
      m: 'mag',
      icon: '🛒',
      k: 'magasin',
      titre: 'Magasin & Ventes',
      sous: 'Caisse, commandes, stock',
      texte:
        'En mode commerce : encaissez vos ventes, gérez les commandes clients, les bons de livraison signés, les remises personnalisées et votre stock en temps réel.',
    },
    {
      icon: '📊',
      k: 'compta',
      titre: 'Comptabilité',
      sous: 'TVA, marges, fiduciaire',
      texte:
        "Visualisez votre chiffre d'affaires, vos marges, votre décompte de T.V.A. aux taux suisses, votre échéancier, vos frais et le coût de votre fiduciaire. Une situation claire et à jour.",
    },
    {
      icon: '🔀',
      k: 'modes',
      titre: 'Trois modes',
      sous: 'Bâtiment, Magasin ou Mix',
      texte:
        "SwissTech s'adapte à votre métier : mode Bâtiment, mode Magasin, ou mode Mix : deux sociétés séparées, le bâtiment et le magasin, sous un seul abonnement.",
    },
    {
      icon: '🏆',
      k: 'why',
      titre: 'Pourquoi SwissTech ?',
      sous: 'Plus complet, moins cher',
      texte:
        'Contrairement aux logiciels qui ne gèrent que la facturation et la comptabilité de bureau, SwissTech réunit aussi le terrain — chantiers, employés, plans, rendez-vous — et le magasin — caisse, stock, code-barres. Le tout sur-mesure, adaptable à tous les métiers, multi-pays, multilingue, et utilisable même hors connexion.',
    },
    {
      icon: '💚',
      k: 'oneprice',
      titre: 'Un seul outil, un seul prix',
      sous: 'Tout-en-un, sans multiplier les abonnements',
      texte:
        "Là où il faut souvent plusieurs logiciels et plusieurs abonnements, SwissTech fait tout dans une seule application : du premier devis sur le chantier jusqu'au dernier franc encaissé en magasin, en passant par la paie suisse et la comptabilité.",
    },
    {
      icon: '🚀',
      k: 'cta',
      titre: 'Essayez SwissTech',
      sous: 'Tout votre métier, une seule app',
      texte:
        'SwissTech : toute votre entreprise, de la première offre au dernier franc encaissé, dans une seule application suisse. Essayez-la dès maintenant.',
    },
  ];
  const _mag = !estMixte && soc && soc.secteur === 'magasin';
  const SLIDES = _SLIDES_ALL.filter(
    (s) => !s.m || estMixte || (_mag ? s.m !== 'bat' : s.m !== 'mag'),
  );
  const SLIDES_TR = {
    en: {
      intro:
        'SwissTech: complete management for your business, in a single app. Quotes, invoices, projects, payroll, stock and accounting. Your whole business, in your pocket.',
      devis:
        'Create professional quotes in seconds, with six layouts. Your client accepts and signs online, and the quote becomes an invoice in one tap.',
      factures:
        'Issue invoices with QR or SEPA payment. Track payments, send reminders, take deposits, and refund overpayments.',
      chantiers:
        'Manage your projects from start to finish: team planning, logged hours, photos, plans, subcontractors, signed change orders, handover and client portal.',
      paie: 'Calculate Swiss salaries automatically: social charges, pension, withholding tax and thirteenth salary. Generate the payslip as a PDF in one click.',
      ouvrier:
        "Your workers log their hours, send daily reports with photos, request leave and see today's site, right from their phone.",
      magasin:
        'In shop mode: take sales at the till, manage customer orders, signed delivery notes, custom discounts and real-time stock.',
      compta:
        "See your turnover, your margins, your VAT statement, your schedule of due dates, your expenses and your accountant's cost. A clear, up-to-date picture.",
      modes:
        'SwissTech adapts to your trade: Construction mode, Shop mode, or Mix mode: two separate companies, construction and shop, under one subscription.',
      why: 'Unlike software that only handles office invoicing and accounting, SwissTech also covers the field and the shop. Tailor-made, multi-country, multilingual, and usable even offline.',
      oneprice:
        'Where you usually need several programs and subscriptions, SwissTech does it all in one app: from the first quote on site to the last dollar collected at the till.',
      cta: 'SwissTech: your whole business, from the first offer to the last payment, in a single app. Try it now.',
    },
    de: {
      intro:
        'SwissTech: die komplette Verwaltung Ihres Unternehmens in einer App. Offerten, Rechnungen, Baustellen, Löhne, Lager und Buchhaltung. Ihr ganzes Geschäft in der Tasche.',
      devis:
        'Erstellen Sie in Sekunden professionelle Offerten mit sechs Layouts. Ihr Kunde nimmt online an und unterschreibt, und die Offerte wird mit einem Tipp zur Rechnung.',
      factures:
        'Stellen Sie Rechnungen mit QR- oder SEPA-Zahlung aus. Verfolgen Sie Zahlungen, senden Sie Mahnungen, erfassen Sie Anzahlungen und erstatten Sie Überzahlungen.',
      chantiers:
        'Steuern Sie Ihre Baustellen von A bis Z: Teamplanung, erfasste Stunden, Fotos, Pläne, Subunternehmer, unterschriebene Nachträge, Abnahme und Kundenportal.',
      paie: 'Berechnen Sie Schweizer Löhne automatisch: Sozialabgaben, Pensionskasse, Quellensteuer und dreizehnter Monatslohn. Erstellen Sie die Lohnabrechnung als PDF mit einem Klick.',
      ouvrier:
        'Ihre Mitarbeiter erfassen ihre Stunden, senden Tagesrapporte mit Fotos, melden Ferien und sehen die heutige Baustelle direkt auf dem Handy.',
      magasin:
        'Im Ladenmodus: kassieren Sie Verkäufe, verwalten Sie Kundenbestellungen, unterschriebene Lieferscheine, individuelle Rabatte und den Lagerbestand in Echtzeit.',
      compta:
        'Sehen Sie Ihren Umsatz, Ihre Margen, Ihre MwSt-Abrechnung, Ihre Fristen, Ihre Kosten und das Honorar Ihres Treuhänders. Ein klares, aktuelles Bild.',
      modes:
        'SwissTech passt sich Ihrem Beruf an: Bau-Modus, Laden-Modus oder Misch-Modus für beide Tätigkeiten mit getrennten Firmendaten.',
      why: 'Anders als Software, die nur Büro-Rechnungen und Buchhaltung macht, deckt SwissTech auch die Baustelle und den Laden ab. Massgeschneidert, mehrsprachig und offline nutzbar.',
      oneprice:
        'Wo man sonst mehrere Programme und Abos braucht, macht SwissTech alles in einer App: von der ersten Offerte bis zum letzten kassierten Franken.',
      cta: 'SwissTech: Ihr ganzes Unternehmen, von der ersten Offerte bis zur letzten Zahlung, in einer App. Probieren Sie es jetzt aus.',
    },
    it: {
      intro:
        "SwissTech: la gestione completa della tua impresa in un'unica app. Preventivi, fatture, cantieri, stipendi, magazzino e contabilità. Tutta la tua attività in tasca.",
      devis:
        'Crea preventivi professionali in pochi secondi, con sei layout. Il cliente accetta e firma online, e il preventivo diventa fattura con un tocco.',
      factures:
        'Emetti fatture con pagamento QR o SEPA. Monitora i pagamenti, invia solleciti, incassa acconti e rimborsa i pagamenti in eccesso.',
      chantiers:
        "Gestisci i cantieri dall'inizio alla fine: pianificazione squadre, ore registrate, foto, piani, subappaltatori, varianti firmate, collaudo e portale cliente.",
      paie: 'Calcola gli stipendi svizzeri automaticamente: oneri sociali, previdenza, imposta alla fonte e tredicesima. Genera la busta paga in PDF con un clic.',
      ouvrier:
        'I tuoi operai registrano le ore, inviano rapporti giornalieri con foto, richiedono le ferie e vedono il cantiere del giorno dal telefono.',
      magasin:
        'In modalità negozio: incassa le vendite, gestisci gli ordini dei clienti, le bolle firmate, gli sconti personalizzati e il magazzino in tempo reale.',
      compta:
        'Visualizza il fatturato, i margini, il rendiconto IVA, le scadenze, le spese e il costo del commercialista. Una situazione chiara e aggiornata.',
      modes:
        'SwissTech si adatta al tuo mestiere: modalità Edilizia, Negozio o Mista per gestire entrambe le attività con dati aziendali distinti.',
      why: "A differenza dei software che gestiscono solo fatturazione e contabilità d'ufficio, SwissTech copre anche il cantiere e il negozio. Su misura, multilingue e utilizzabile offline.",
      oneprice:
        "Dove di solito servono più programmi e abbonamenti, SwissTech fa tutto in un'app: dal primo preventivo in cantiere all'ultimo incasso.",
      cta: "SwissTech: tutta la tua impresa, dalla prima offerta all'ultimo pagamento, in un'unica app. Provala subito.",
    },
    es: {
      intro:
        'SwissTech: la gestión completa de tu empresa en una sola app. Presupuestos, facturas, obras, salarios, inventario y contabilidad. Todo tu negocio en el bolsillo.',
      devis:
        'Crea presupuestos profesionales en segundos, con seis estilos. Tu cliente acepta y firma en línea, y el presupuesto se convierte en factura con un toque.',
      factures:
        'Emite facturas con pago QR o SEPA. Controla los pagos, envía recordatorios, cobra anticipos y devuelve los pagos en exceso.',
      chantiers:
        'Gestiona tus obras de principio a fin: planificación de equipos, horas registradas, fotos, planos, subcontratistas, anexos firmados, recepción y portal del cliente.',
      paie: 'Calcula los salarios suizos automáticamente: cargas sociales, pensión, impuesto en la fuente y decimotercer salario. Genera la nómina en PDF con un clic.',
      ouvrier:
        'Tus trabajadores registran sus horas, envían partes diarios con fotos, solicitan vacaciones y ven la obra del día desde su teléfono.',
      magasin:
        'En modo tienda: cobra tus ventas, gestiona los pedidos de clientes, los albaranes firmados, los descuentos personalizados y el stock en tiempo real.',
      compta:
        'Visualiza tu facturación, tus márgenes, tu declaración de IVA, tu calendario de vencimientos, tus gastos y el coste de tu gestor. Una situación clara y al día.',
      modes:
        'SwissTech se adapta a tu oficio: modo Construcción, modo Tienda o modo Mixto: dos empresas separadas, construcción y tienda, con una sola suscripción.',
      why: 'A diferencia del software que solo gestiona facturación y contabilidad de oficina, SwissTech cubre también el terreno y la tienda. A medida, multilingüe y utilizable sin conexión.',
      oneprice:
        'Donde normalmente hacen falta varios programas y suscripciones, SwissTech lo hace todo en una app: del primer presupuesto al último cobro.',
      cta: 'SwissTech: toda tu empresa, desde la primera oferta hasta el último pago, en una sola app. Pruébala ahora.',
    },
    pt: {
      intro:
        'SwissTech: a gestão completa da sua empresa numa só app. Orçamentos, faturas, obras, salários, stock e contabilidade. Todo o seu negócio no bolso.',
      devis:
        'Crie orçamentos profissionais em segundos, com seis estilos. O cliente aceita e assina online, e o orçamento torna-se fatura com um toque.',
      factures:
        'Emita faturas com pagamento QR ou SEPA. Acompanhe pagamentos, envie lembretes, receba adiantamentos e devolva pagamentos em excesso.',
      chantiers:
        'Faça a gestão das obras do início ao fim: planeamento de equipas, horas registadas, fotos, plantas, subempreiteiros, adendas assinadas, receção e portal do cliente.',
      paie: 'Calcule os salários suíços automaticamente: encargos sociais, pensão, imposto na fonte e décimo terceiro. Gere o recibo de vencimento em PDF com um clique.',
      ouvrier:
        'Os seus trabalhadores registam as horas, enviam relatórios diários com fotos, pedem férias e veem a obra do dia no telemóvel.',
      magasin:
        'No modo loja: receba as vendas, faça a gestão das encomendas, das guias assinadas, dos descontos personalizados e do stock em tempo real.',
      compta:
        'Veja a sua faturação, as suas margens, a declaração de IVA, os prazos, as despesas e o custo do contabilista. Uma situação clara e atualizada.',
      modes:
        'A SwissTech adapta-se à sua atividade: modo Construção, modo Loja ou modo Misto para gerir as duas atividades com dados de empresa distintos.',
      why: 'Ao contrário dos programas que só tratam da faturação e contabilidade de escritório, a SwissTech cobre também o terreno e a loja. À medida, multilingue e utilizável offline.',
      oneprice:
        'Onde normalmente são precisos vários programas e subscrições, a SwissTech faz tudo numa app: do primeiro orçamento ao último recebimento.',
      cta: 'SwissTech: toda a sua empresa, da primeira proposta ao último pagamento, numa só app. Experimente agora.',
    },
  };
  const _BCP = { fr: 'fr-FR', en: 'en-US', de: 'de-DE', it: 'it-IT', es: 'es-ES', pt: 'pt-PT' };
  const slideTxt = (sl) =>
    (SLIDES_TR[LANG] && SLIDES_TR[LANG][sl.k]) ||
    (LANG === 'fr' ? sl.texte : SLIDES_TR.en[sl.k] || sl.texte);
  const slideLang = () =>
    SLIDES_TR[LANG] ? _BCP[LANG] || 'en-US' : LANG === 'fr' ? 'fr-FR' : 'en-US';
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const idxRef = useRef(0),
    playRef = useRef(false),
    voiceRef = useRef(true),
    timerRef = useRef(null);
  useEffect(() => {
    idxRef.current = idx;
  }, [idx]);
  useEffect(() => {
    playRef.current = playing;
  }, [playing]);
  useEffect(() => {
    voiceRef.current = voiceOn;
  }, [voiceOn]);
  const last = SLIDES.length - 1;
  const hasTTS = () =>
    typeof window !== 'undefined' &&
    window.speechSynthesis &&
    typeof window.SpeechSynthesisUtterance !== 'undefined';
  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  const stopAll = () => {
    clearTimer();
    if (hasTTS()) {
      try {
        window.speechSynthesis.cancel();
      } catch (e) {}
    }
  };
  // démarre la lecture de la diapo i — APPELÉ DIRECTEMENT depuis l'appui (indispensable sur iPhone)
  const playFrom = (i) => {
    clearTimer();
    if (i > last) {
      setPlaying(false);
      playRef.current = false;
      return;
    }
    setIdx(i);
    idxRef.current = i;
    setPlaying(true);
    playRef.current = true;
    const advance = () => {
      if (!playRef.current) return;
      const n = idxRef.current + 1;
      if (n > last) {
        setPlaying(false);
        playRef.current = false;
      } else {
        playFrom(n);
      }
    };
    if (voiceRef.current && hasTTS()) {
      try {
        const ss = window.speechSynthesis;
        try {
          ss.cancel();
        } catch (e) {}
        const u = new window.SpeechSynthesisUtterance(slideTxt(SLIDES[i]));
        u.lang = slideLang();
        u.rate = 0.97;
        u.pitch = 1;
        const vs = ss.getVoices() || [];
        const _lc = (u.lang || 'en').slice(0, 2).toLowerCase();
        const vm = vs.find((v) => v.lang && v.lang.toLowerCase().slice(0, 2) === _lc);
        if (vm) u.voice = vm;
        u.onend = advance;
        u.onerror = () => {
          clearTimer();
          timerRef.current = setTimeout(advance, 4800);
        };
        ss.speak(u);
        // filet de sécurité : si la voix ne démarre pas du tout, on enchaîne quand même
        clearTimer();
        timerRef.current = setTimeout(() => {
          if (playRef.current && !ss.speaking) {
            advance();
          }
        }, 9000);
      } catch (e) {
        clearTimer();
        timerRef.current = setTimeout(advance, 4800);
      }
    } else {
      clearTimer();
      timerRef.current = setTimeout(advance, 4800);
    }
  };
  const pause = () => {
    setPlaying(false);
    playRef.current = false;
    stopAll();
  };
  const toggle = () => {
    if (playing) {
      pause();
    } else {
      playFrom(idx >= last ? 0 : idx);
    }
  };
  const go = (d) => {
    const n = Math.min(last, Math.max(0, idx + d));
    if (playing) {
      playFrom(n);
    } else {
      setIdx(n);
    }
  };
  const jump = (i) => {
    if (playing) {
      playFrom(i);
    } else {
      setIdx(i);
    }
  };
  const toggleVoice = () => {
    const nv = !voiceOn;
    setVoiceOn(nv);
    voiceRef.current = nv;
    if (playing) {
      playFrom(idx);
    }
  };
  useEffect(() => () => stopAll(), []);
  const s = SLIDES[idx];
  const btnS = {
    width: 46,
    height: 46,
    borderRadius: 99,
    border: '1px solid ' + C.border,
    background: '#fff',
    color: C.ink,
    fontSize: 16,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  return (
    <Shell
      active="plus"
      onNav={onNav}
      soc={soc}
      pageTitle="🎬 Présentation"
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      <div
        style={{
          background: 'linear-gradient(160deg,' + C.navy + ',' + C.teal + ')',
          borderRadius: 18,
          padding: '32px 22px',
          color: '#fff',
          minHeight: 330,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          boxShadow: '0 10px 30px rgba(0,0,0,.18)',
        }}
      >
        <div style={{ fontSize: 64, marginBottom: 14 }}>{s.icon}</div>
        <div style={{ fontSize: 24, fontWeight: 900, marginBottom: 6 }}>{s.titre}</div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            opacity: 0.85,
            marginBottom: 16,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          {s.sous}
        </div>
        <div style={{ fontSize: 13.5, lineHeight: 1.65, maxWidth: 340, opacity: 0.97 }}>
          {slideTxt(s)}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, margin: '16px 0' }}>
        {SLIDES.map((_, i) => (
          <div
            key={i}
            onClick={() => jump(i)}
            style={{
              width: i === idx ? 22 : 8,
              height: 8,
              borderRadius: 99,
              background: i === idx ? C.teal : C.border,
              cursor: 'pointer',
              transition: 'all .2s',
            }}
          />
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
        <button
          onClick={() => go(-1)}
          disabled={idx === 0}
          style={{ ...btnS, opacity: idx === 0 ? 0.4 : 1 }}
        >
          ◀
        </button>
        <button
          onClick={toggle}
          style={{
            ...btnS,
            width: 64,
            height: 64,
            background: C.teal,
            color: '#fff',
            fontSize: 24,
            border: 'none',
          }}
        >
          {playing ? '⏸' : '▶'}
        </button>
        <button
          onClick={() => go(1)}
          disabled={idx === last}
          style={{ ...btnS, opacity: idx === last ? 0.4 : 1 }}
        >
          ▶
        </button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
        <button
          onClick={toggleVoice}
          style={{
            border: '1px solid ' + C.border,
            background: voiceOn ? '#e6f7f4' : '#fff',
            color: voiceOn ? C.teal : C.muted,
            borderRadius: 99,
            padding: '8px 16px',
            fontSize: 11,
            fontWeight: 800,
            cursor: 'pointer',
          }}
        >
          {voiceOn ? '🔊 Voix activée (lecture auto)' : '🔇 Voix coupée (défilement auto)'}
        </button>
      </div>
      <div
        style={{
          fontSize: 9,
          color: C.muted,
          textAlign: 'center',
          marginTop: 14,
          lineHeight: 1.5,
        }}
      >
        Appuyez sur ▶ pour lancer. Sur iPhone : montez le volume et désactivez le bouton silencieux.
        Si vous n'entendez rien la 1re fois, appuyez à nouveau sur ▶. Pour une pub : lancez et
        filmez l'écran (enregistrement d'écran).
      </div>
    </Shell>
  );
}
